/* Native Shopify forms handle cart submission. This element only enhances selection. */
(() => {
  if (customElements.get('ve-product')) return;

  class ViuProduct extends HTMLElement {
    connectedCallback() {
      // The editor can connect a custom element before its children finish parsing.
      this.startTimer = setTimeout(() => this.start(), 0);
    }

    start() {
      if (!this.isConnected || this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.querySelector('[data-ve-variant-go]')?.setAttribute('hidden', '');
      this.querySelectorAll('[data-ve-purchase]').forEach(purchase => {
        purchase.querySelector('[data-ve-modes]')?.removeAttribute('hidden');
        purchase.querySelector('[data-ve-plan-once]')?.setAttribute('hidden', '');
        purchase.dataset.enhanced = '';
        this.syncPurchase(purchase);
      });
      this.addEventListener('change', event => {
        if (event.target.matches('[data-ve-variant]')) {
          this.querySelectorAll('[data-ve-variant-panel]').forEach(panel => {
            panel.hidden = panel.dataset.veVariantPanel !== event.target.value;
          });
          const shown = this.querySelector('[data-ve-variant-panel]:not([hidden]) [data-ve-purchase]');
          if (shown) this.syncPurchase(shown);
          return;
        }
        const purchase = event.target.closest('[data-ve-purchase]');
        if (!purchase) return;
        if (event.target.name === 've-purchase-mode') {
          const radios = [...purchase.querySelectorAll('[name="selling_plan"]')];
          const target = event.target.value === 'once'
            ? radios.find(radio => !radio.value)
            : radios.find(radio => radio.value && radio.value === purchase.dataset.lastPlan) || radios.find(radio => radio.value);
          if (target) target.checked = true;
        }
        this.syncPurchase(purchase);
      }, { signal });
      this.querySelector('[data-ve-return]')?.addEventListener('click', () => {
        const control = this.querySelector('[data-ve-variant-panel]:not([hidden]) input[type="radio"]') || this.querySelector('[data-ve-variant-panel]:not([hidden]) button');
        this.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        control?.focus({ preventScroll: true });
      }, { signal });
      const sticky = this.querySelector('[data-ve-sticky]');
      // Disable fixed shopping chrome inside the theme editor; keep blocks selectable.
      if (sticky && !window.Shopify?.designMode && 'IntersectionObserver' in window) {
        // Keep a thumb-reachable shopping action unless the purchase module or another
        // primary call to action (such as the hero button) is already on screen.
        const visible = new Set();
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => entry.isIntersecting ? visible.add(entry.target) : visible.delete(entry.target));
          sticky.hidden = visible.size > 0;
        });
        [this, ...document.querySelectorAll('[data-ve-cta]')].forEach(target => this.observer.observe(target));
      }
    }

    syncPurchase(purchase) {
      const radios = [...purchase.querySelectorAll('[name="selling_plan"]')];
      const plans = radios.filter(radio => radio.value);
      const selected = radios.find(radio => radio.checked);
      const subscribed = Boolean(selected?.value);
      const displayPlan = subscribed ? selected : plans.find(radio => radio.value === purchase.dataset.lastPlan) || plans[0];
      // One-time purchases submit no selling_plan field at all.
      if (radios.length) {
        radios.forEach(radio => { radio.disabled = !subscribed; });
        if (subscribed) purchase.dataset.lastPlan = selected.value;
        purchase.querySelector('[data-ve-plan-picker]').hidden = !subscribed;
      }
      purchase.querySelectorAll('[name="ve-purchase-mode"]').forEach(radio => {
        radio.checked = radio.value === (subscribed ? 'subscription' : 'once');
      });
      const price = subscribed ? selected.dataset.price : purchase.dataset.oncePrice;
      const stickyPrice = this.querySelector('[data-ve-sticky-price]');
      if (stickyPrice && !purchase.closest('[data-ve-variant-panel]')?.hidden) stickyPrice.textContent = subscribed ? `${price} · ${selected.dataset.label}` : price;
      purchase.querySelector('[data-ve-price]').textContent = price;
      purchase.querySelector('[data-ve-price]').hidden = false;
      purchase.querySelector('[data-ve-price-separator]').hidden = false;
      purchase.querySelector('[data-ve-summary]').hidden = false;
      const subPrice = purchase.querySelector('[data-ve-sub-price]');
      if (subPrice) subPrice.textContent = displayPlan?.dataset.price || '';
      const badge = purchase.querySelector('[data-ve-saving-badge]');
      if (badge) badge.textContent = displayPlan?.dataset.percent || 'Recurring deliveries';
      const compare = purchase.querySelector('[data-ve-compare]');
      if (compare) {
        compare.hidden = !displayPlan?.dataset.saving;
        compare.textContent = displayPlan?.dataset.compare || '';
      }
      purchase.querySelector('[data-ve-summary]').textContent = subscribed
        ? [selected.dataset.saving, selected.dataset.unit].filter(Boolean).join(' · ')
        : purchase.dataset.onceUnit || '';
      purchase.querySelectorAll('[data-ve-description]').forEach(description => {
        description.hidden = !subscribed || description.dataset.veDescription !== selected.value;
      });
      const disclosure = purchase.querySelector('[data-ve-disclosure]');
      if (disclosure) disclosure.hidden = !subscribed;
      purchase.querySelector('.ve-buy').disabled = purchase.dataset.available !== 'true' || (purchase.dataset.requiresPlan === 'true' && !subscribed);
    }

    disconnectedCallback() {
      clearTimeout(this.startTimer);
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
    }
  }
  customElements.define('ve-product', ViuProduct);
})();
